import { useEffect } from "react";

/** Update <title>, <meta name="description">, canonical URL and the matching
 *  OG/Twitter tags for the currently-rendered page.
 *
 *  Usage: `usePageMeta({ title: "Browse Creators — CheatCraft", description: "..." })`
 *  The homepage defaults live in `public/index.html` — pages that opt in
 *  override only what they need. On unmount we restore the defaults.
 */
const DEFAULTS = {
  title: "CheatCraft — The Marketplace for Cheat Providers & Media Creators",
  description:
    "Stop getting scammed. CheatCraft connects cheat software providers with trusted media creators. Escrow payments, watermark protection, secure file sharing. Find your creator today.",
  ogTitle: "CheatCraft — Cheats Meet Creators",
  ogDescription:
    "The secure marketplace connecting cheat providers with media creators. Escrow payments, watermark protection, guaranteed delivery.",
};

function upsertMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [key, val] = selector.replace(/^meta\[|\]$/g, "").split("=");
    el.setAttribute(key, val.replace(/"/g, ""));
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function usePageMeta({ title, description, canonicalPath, ogTitle, ogDescription, ogImage } = {}) {
  useEffect(() => {
    const prev = {
      title: document.title,
    };

    if (title) document.title = title;

    upsertMeta('meta[name="description"]', "content", description || DEFAULTS.description);

    upsertMeta('meta[property="og:title"]', "content", ogTitle || title || DEFAULTS.ogTitle);
    upsertMeta('meta[property="og:description"]', "content", ogDescription || description || DEFAULTS.ogDescription);
    if (ogImage) upsertMeta('meta[property="og:image"]', "content", ogImage);

    upsertMeta('meta[name="twitter:title"]', "content", ogTitle || title || DEFAULTS.ogTitle);
    upsertMeta('meta[name="twitter:description"]', "content", ogDescription || description || DEFAULTS.ogDescription);
    if (ogImage) upsertMeta('meta[name="twitter:image"]', "content", ogImage);

    if (canonicalPath !== undefined) {
      const base = "https://cheatcraft.net";
      const href = canonicalPath ? (canonicalPath.startsWith("http") ? canonicalPath : `${base}${canonicalPath}`) : base + "/";
      upsertLink("canonical", href);
      upsertMeta('meta[property="og:url"]', "content", href);
    }

    return () => {
      document.title = prev.title;
    };
  }, [title, description, canonicalPath, ogTitle, ogDescription, ogImage]);
}

export default usePageMeta;
